import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import DashboardSectionTile from '../../core/DashboardSectionTile';
import { BackButton, detailsHStackLayoutProps, FieldGroup, PreambleText, VStackLayout } from '../Components';
import { Box, Button, Flex, HStack, Input, Text, VStack } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import Constants from '../../../Constants';
import { Alert } from '../../ui/alert';
import { getTenantFromDomain } from '../../../utils';

const tenant = getTenantFromDomain();

function sellQuantityInCartons(sellQuantity, sellUnitOfMeasure) {
  if (sellQuantity === null || sellQuantity <= 0) {
    return 0;
  }
  if (sellUnitOfMeasure === 'bin') {
    return 0;
  }
  if (sellUnitOfMeasure === 'kgs') {
    return sellQuantity / 27.2;
  }
  if (sellUnitOfMeasure === 'lbs') {
    return sellQuantity / 60;
  }
  return sellQuantity;
}

const DetailContainer = ({ children }) => (
  <VStack align="stretch" gap="28px">
    {children}
  </VStack>
);
DetailContainer.propTypes = {
  children: PropTypes.node.isRequired,
};

const DetailItem = ({ label, detail }) => (
  <Box>
    <PreambleText>{label}</PreambleText>
    <Text>{detail}</Text>
  </Box>
);
DetailItem.propTypes = {
  label: PropTypes.string.isRequired,
  detail: PropTypes.string.isRequired,
};

const EntrapmentForm = ({ order, token, grinderPoNumber, onSave }) => {
  const navigate = useNavigate();
  const [entrapments, setEntrapments] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleEntrapmentChange = (index, value) => {
    const updatedEntrapments = [...entrapments];
    updatedEntrapments[index].entrapment = value;
    setEntrapments(updatedEntrapments);
  };

  const handleExposedMeatChange = (index, value) => {
    const updatedEntrapments = [...entrapments];
    updatedEntrapments[index].numberOfCartonsWithExposedMeat = value;
    setEntrapments(updatedEntrapments);
  };

  const handleLinerStuckChange = (index, value) => {
    const updatedEntrapments = [...entrapments];
    updatedEntrapments[index].numberOfCartonsWithLinerStuckToGlue = value;
    setEntrapments(updatedEntrapments);
  };

  const onClose = () => {
    if (typeof onSave === 'function') {
      onSave();
    }
    navigate('/ncr');
  };

  const handleSave = async () => {
    if (!entrapments || entrapments.length === 0) {
      setErrorMessage('No entrapment data available');
      return;
    }

    const item = entrapments[0];
    const { entrapment, numberOfCartonsWithExposedMeat, numberOfCartonsWithLinerStuckToGlue, maxAllowedValue } = item;
    
    const invalidFields = [];
    
    if (entrapment > maxAllowedValue) {
      invalidFields.push('Number of cartons entrapped');
    }
    if (numberOfCartonsWithExposedMeat > maxAllowedValue) {
      invalidFields.push('Number of Cartons with Exposed Meat');
    }
    if (numberOfCartonsWithLinerStuckToGlue > maxAllowedValue) {
      invalidFields.push('Number of Cartons with Liner Stuck to Glue');
    }
    
    if (invalidFields.length > 0) {
      setErrorMessage(`The following field(s) exceed the maximum allowed value: ${invalidFields.join(', ')}`);
      return;
    }

    try {
      setIsSaving(true);
      setErrorMessage('');
      const endpointUrl = `${Constants.URL}entrapment`;
      const headers = new Headers({
        'Content-Type': 'application/json',
        Authorization: `Token ${token}`,
      });

      const response = await fetch(endpointUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          grinder_po_number: grinderPoNumber,
          entrapment: entrapment || 0,
          cartons_exposed_meat: numberOfCartonsWithExposedMeat || 0,
          cartons_glue_stuck_to_liner: numberOfCartonsWithLinerStuckToGlue || 0,
        }),
      });
      if (response.status === 201 || response.status === 200) {
        onClose();
      } else {
        const data = await response.json();
        setErrorMessage(data.message);
      }
    } catch (error) {
      setErrorMessage(error?.message ?? 'An error occurred while saving the entrapment details');
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const getMaxAllowedValue = (rawSellQuantity, sellUOM) => {
    if (!rawSellQuantity || rawSellQuantity <= 0) {
      return 0;
    }
    const uomLower = sellUOM?.toLowerCase();
    if (uomLower === 'lbs' || uomLower === 'lb') {
      return rawSellQuantity / 60;
    }
    if (uomLower === 'kgs' || uomLower === 'kg') {
      return rawSellQuantity / 27.2;
    }
    return rawSellQuantity;
  };

  useEffect(() => {
    if (!order) {
      return;
    }
    const totalSellQuantity = order.lines.reduce((sum, line) => {
      if (line?.sellQuantity !== null && line?.sellQuantity !== undefined) {
        return sum + line?.sellQuantity;
      }
      return sum;
    }, 0);

    const totalSellQuantityInCartons = sellQuantityInCartons(totalSellQuantity, order?.sellUnitOfMeasure?.name);
    const maxAllowedValue = getMaxAllowedValue(totalSellQuantity, order?.sellUnitOfMeasure?.name);

    setEntrapments([
      {
        grinderPoNumber,
        totalSellQuantity: totalSellQuantityInCartons || 0,
        rawSellQuantity: totalSellQuantity || 0,
        sellUOM: order?.sellUnitOfMeasure?.name || '',
        maxAllowedValue: maxAllowedValue || 0,
        entrapment: typeof order?.entrapment === 'number' ? order.entrapment : 0,
        numberOfCartonsWithExposedMeat: typeof order?.cartonsExposedMeat === 'number' ? order.cartonsExposedMeat : 0,
        numberOfCartonsWithLinerStuckToGlue: typeof order?.cartonsGlueStuckToLiner === 'number' ? order.cartonsGlueStuckToLiner : 0,
      },
    ]);
  }, [order, grinderPoNumber]);

  return (
    <DashboardSectionTile
      title="Entrapment Detials"
      padding="22px 33px 22px 20px"
      marginTop="20px"
      position="relative"
      headerFlexStyle={{
        justifyContent: 'space-between',
      }}
    >
      <VStackLayout>
        <Box>
          {entrapments.map((item, index) => (
            <VStack key={index} align="stretch" gap="16px" marginTop="18px">
              <HStack gap="32px" align="flex-start">
                <FieldGroup label="Grinder PO Number">
                  <Input value={item.grinderPoNumber} disabled />
                </FieldGroup>
                <FieldGroup label="Total Sell Quantity(Cartons)">
                  <Input value={item.totalSellQuantity.toFixed(2)} disabled />
                </FieldGroup>
                <FieldGroup label="Number of cartons entrapped">
                  <Box>
                    <Input
                      value={item.entrapment}
                      type="number"
                      onChange={e => handleEntrapmentChange(index, +e.target.value)}
                      borderColor={item.entrapment > item.maxAllowedValue ? 'red.500' : undefined}
                    />
                    {item.entrapment > item.maxAllowedValue && (
                      <Text fontSize="sm" color="red.500" mt="1">
                        More than Load Total Carton Count not allowed
                      </Text>
                    )}
                  </Box>
                </FieldGroup>
              </HStack>
              {tenant !== 'cfc' && (
                <HStack gap="32px" align="flex-start">
                  <FieldGroup label="Number of Cartons with Exposed Meat">
                    <Box>
                      <Input
                        value={item.numberOfCartonsWithExposedMeat || 0}
                        type="number"
                        onChange={e => handleExposedMeatChange(index, +e.target.value)}
                        borderColor={item.numberOfCartonsWithExposedMeat > item.maxAllowedValue ? 'red.500' : undefined}
                      />
                      {item.numberOfCartonsWithExposedMeat > item.maxAllowedValue && (
                        <Text fontSize="sm" color="red.500" mt="1">
                          More than Load Total Carton Count not allowed
                        </Text>
                      )}
                    </Box>
                  </FieldGroup>
                  <FieldGroup label="Number of Cartons with Liner Stuck to Glue">
                    <Box>
                      <Input
                        value={item.numberOfCartonsWithLinerStuckToGlue || 0}
                        type="number"
                        onChange={e => handleLinerStuckChange(index, +e.target.value)}
                        borderColor={item.numberOfCartonsWithLinerStuckToGlue > item.maxAllowedValue ? 'red.500' : undefined}
                      />
                      {item.numberOfCartonsWithLinerStuckToGlue > item.maxAllowedValue && (
                        <Text fontSize="sm" color="red.500" mt="1">
                          More than Load Total Carton Count not allowed
                        </Text>
                      )}
                    </Box>
                  </FieldGroup>
                </HStack>
              )}
            </VStack>
          ))}
        </Box>

        {errorMessage && (
          <Alert
            status="error"
            marginTop="23px"
            backgroundColor="#FED7D7"
            borderRadius="6px"
            fontWeight="400"
            fontSize="16px"
            title={errorMessage}
            closable
            onClose={() => {
              setErrorMessage('');
            }}
          />
        )}

        <Flex justifyContent="space-between" marginY="35px">
          <BackButton fontSize="16px" onClick={onClose} />

          <Button
            colorScheme="actionPrimary"
            fontWeight="700"
            width="115px"
            height="40px"
            onClick={handleSave}
            loading={isSaving}
          >
            Save
          </Button>
        </Flex>
      </VStackLayout>
    </DashboardSectionTile>
  );
};

export default EntrapmentForm;
EntrapmentForm.propTypes = {
  order: PropTypes.object.isRequired,
  token: PropTypes.string.isRequired,
  grinderPoNumber: PropTypes.string.isRequired,
  onSave: PropTypes.func,
};
